import React, { useEffect, useRef } from "react";

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function ensureTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.turnstile));
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function TurnstileWidget({
  siteKey,
  onVerify,
  onExpire,
  onError,
}) {
  const hostRef = useRef(null);
  const widgetIdRef = useRef(null);

  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let mounted = true;

    async function mountWidget() {
      if (!siteKey || !hostRef.current) return;
      if (widgetIdRef.current !== null) return;

      try {
        const turnstile = await ensureTurnstileScript();
        if (!mounted || !turnstile || !hostRef.current) return;

        widgetIdRef.current = turnstile.render(hostRef.current, {
          sitekey: siteKey,
          theme: "light",
          callback: (token) => {
            if (onVerifyRef.current) onVerifyRef.current(token);
          },
          "expired-callback": () => {
            if (onExpireRef.current) onExpireRef.current();
          },
          "error-callback": () => {
            if (onErrorRef.current) onErrorRef.current();
          },
        });
      } catch (e) {
        if (onErrorRef.current) onErrorRef.current();
      }
    }

    mountWidget();

    return () => {
      mounted = false;
    };
  }, [siteKey]);

  return <div ref={hostRef} />;
}
